import { action, computed, IObservableArray, observable } from "mobx";
import { inject, observer } from "mobx-react";
import * as React from "react";
import { RouteComponentProps } from "react-router-dom";
import { AddButton } from "../Buttons/AddButton";
import { ButtonGroup } from "../Buttons/ButtonGroup";
import { CloseButton } from "../Buttons/CloseButton";
import { DeleteButton } from "../Buttons/DeleteButton";
import { FileLinkButton } from "../Buttons/FileLinkButton";
import { SaveButton } from "../Buttons/SaveButton";
import { SelectionButton } from "../Buttons/SelectionButton";
import { ShowAreaAsideButton } from "../Buttons/ShowAreaAsideButton";
import * as DocumentManagement from "../DocumentManagement";
import { AreaBody } from "../General/AreaBody";
import { ContentGroup } from "../General/ContentGroup";
import { Toolbar } from "../General/Toolbar";
import { WizardBox } from "../General/WizardBox";
import { IconType, MimeTypeIcon } from "../Icons/MimeTypeIcon";
import { FileUpload, FileUploadInfo } from "../Inputs/FileUpload";
import { InputDateComponent } from "../Inputs/InputDateComponent";
import { InputTextComponent } from "../Inputs/InputTextComponent";
import * as UiStore from "../States/AreaUiInfo";
import { AppStore } from "../Stores/AppStore";
import { DialogResult, DialogStore } from "../Stores/DialogStore";
import { DocumentStoreImpl } from "../Stores/DocumentStore";
import { NotificationStore } from "../Stores/NotificationStore";
import { Strings } from "../Stores/StringsStore";
import { UiRepos } from "../Stores/UiStore";
import { Urls } from "../Urls";
import * as Utilities from "../Utilities";
import { FileLanguageSelection } from "./FileLanguageSelection";
import { SelectCheckbox } from "./SelectCheckbox";
import { WizardBoxType } from "../General/WizardBox";
import { ChangeLanguageButton } from "../Buttons/ChangeLanguageButton";

interface FileEditProps
	extends RouteComponentProps<IFileEditPropsParams>,
		UiStore.IAreaUiInfo {
	documentStore?: DocumentStoreImpl;
}

interface IFileEditPropsParams {
	collection: string;
	document: string;
	language: string;
}

@inject("documentStore")
@inject("areaUiInfo")
@observer
export class FileEdit extends React.Component<FileEditProps, {}> {
	files: IObservableArray<DocumentManagement.FileDetails> =
		observable.array<DocumentManagement.FileDetails>();
	uploadedFiles: IObservableArray<DocumentManagement.FileDetails> =
		observable.array<DocumentManagement.FileDetails>();
	@observable fileUploadInProgress = false;
	@observable hash = "";
	wizardBoxType: WizardBoxType;

	componentWillMount() {
		this.getFiles();
		this.props.documentStore.handleFilesDelete = this.handleDelete;
	}

	componentDidMount() {
		this.setTitle();
	}

	componentWillUnmount() {
		if (this.props.documentStore.fileSelectionMode) {
			this.handleSelectClick();
		}
	}

	@action setTitle = () => {
		AppStore.setTitle(Strings.documentManagement.fileEdit.title);
	};

	getFiles = () => {
		AppStore.isBusyModelLoading = true;
		DocumentManagement.getDocumentFilesAsync(
			this.props.match.params.document
		)
			.then((model) => {
				this.files.push(...model.files);
				this.hash = JSON.stringify(this.files);
				NotificationStore.clearMessage();
				AppStore.isBusyModelLoading = false;
			})
			.catch((error) => {
				DialogStore.showError(Strings.general.errorDialogTitle, error);
			});
	};

	loadUiSettings() {
		this.props.areaUiInfo.contentGroupAccordionStatus.replace(
			UiRepos.filesUiInfo.contentGroupAccordionStatus
		);
	}

	get languageOptions() {
		let languages: LanguageOption[] = [];
		let invariantOption = new LanguageOption();
		invariantOption.value = "";
		invariantOption.label = `${Strings.general.invariantLanguageCaption} (${
			this.files.filter((f) => !f.language).length
		})`;
		invariantOption.region = Strings.general.invariantLanguageRegion;
		languages.push(invariantOption);
		AppStore.context.contentLanguages
			.sort((a, b) => {
				if (a.name === null) return -1;
				if (b.name === null) return 1;
				if (a.caption < b.caption) return -1;
				if (a.caption > b.caption) return 1;
				return 0;
			})
			.forEach((l) => {
				let option = new LanguageOption();
				option.value = l.name;
				option.label = `${l.caption} (${
					this.files.filter((f) => f.language === l.name).length
				})`;
				option.region = l.region;
				languages.push(option);
			});
		return languages;
	}

	@computed get canSave(): boolean {
		let hasChanged = JSON.stringify(this.files) !== this.hash;
		let canSave =
			!this.fileUploadInProgress &&
			hasChanged &&
			!this.files.some((f) => !f.name.value.trim());
		AppStore.isDirtyMode = canSave;
		AppStore.isDirtyChange = hasChanged;
		AppStore.saveOnLeave = this.handleSaveClick;
		return canSave;
	}

	handleDelete = () => {
		let deleteTitle =
			this.props.documentStore.selectedFiles.length > 1
				? Strings.documentManagement.fileEdit
						.deleteMultipleFilesDialogTitle
				: Utilities.format(
						Strings.general.dialogDeleteTitle,
						this.props.documentStore.selectedFiles[0].name.value
				  );
		let deleteText =
			this.props.documentStore.selectedFiles.length > 1
				? Strings.documentManagement.fileEdit
						.deleteMultipleFilesDialogText
				: Utilities.format(
						Strings.general.dialogDeleteText,
						this.props.documentStore.selectedFiles[0].name.value
				  );
		DialogStore.show(deleteTitle, deleteText).then((result) => {
			if (result === DialogResult.Yes) {
				this.props.documentStore.selectedFiles.forEach((f) => {
					this.files.remove(f);
					this.uploadedFiles.remove(f);
				});
				this.props.documentStore.selectedFiles.clear();
				this.props.documentStore.fileSelectionMode = false;
			}
		});
	};

	handleChangeLanguageClick = () => {
		this.wizardBoxType = WizardBoxType.Confirm;
		this.props.documentStore.selectedFileLanguage = undefined;
		this.props.documentStore.fileLanguageWizardStore.onConfirmExternal =
			this.changeLanguage;
		this.props.documentStore.fileLanguageWizardStore.open();
	};

	changeLanguage = () => {
		this.props.documentStore.selectedFiles.forEach((file) => {
			file.language = this.props.documentStore.selectedFileLanguage;
		});
		this.props.documentStore.selectedFiles.clear();
		this.props.documentStore.fileSelectionMode = false;
	};

	handleAdd = () => {
		this.wizardBoxType = WizardBoxType.Next;
		this.props.documentStore.selectedFileLanguage = undefined;
		this.props.documentStore.fileLanguageWizardStore.onConfirmExternal =
			this.handleFileUploadClick;
		this.props.documentStore.fileLanguageWizardStore.open();
	};

	handleSelectClick = () => {
		this.props.documentStore.fileSelectionMode =
			!this.props.documentStore.fileSelectionMode;
		if (!this.props.documentStore.fileSelectionMode) {
			this.props.documentStore.selectedFiles.clear();
		}
	};

	handleCancel = () => {
		let parts = window.location.href.split("?");
		let queryString = parts.length > 1 ? "?" + parts[1] : "";
		let fileLink = `${Urls.collections}/${this.props.match.params.collection}/documents/${this.props.match.params.document}`;
		fileLink += queryString;
		AppStore.keepOnPage(fileLink, null, this.props.areaUiInfo.closeExpand);
	};

	handleSaveClick = () => {
		if (this.canSave) {
			AppStore.isBusy = true;
			let update = new DocumentManagement.DocumentFilesUpdate();
			update.guid = this.props.match.params.document;
			update.files = this.files.map((f) => {
				let fileUpdate = new DocumentManagement.FileDetailsUpdate();
				fileUpdate.guid = f.guid;
				fileUpdate.language = f.language;
				fileUpdate.mimeType = f.mimeType;
				fileUpdate.size = f.size;
				fileUpdate.name = f.name.value;
				fileUpdate.originalName = f.originalName;
				fileUpdate.created = new Date(f.created);
				fileUpdate.lastModified = f.lastModified;
				fileUpdate.validFrom =
					f.validFrom.value && f.validFrom.value.valueOf()
						? f.validFrom.value
						: null;
				fileUpdate.validUntil =
					f.validUntil.value && f.validUntil.value.valueOf()
						? f.validUntil.value
						: null;
				return fileUpdate;
			});
			DocumentManagement.updateDocumentFilesAsync(update)
				.then((result) => {
					AppStore.isBusy = false;
					if (result.isValid) {
						this.files.replace(result.files);
						this.hash = JSON.stringify(this.files);
						this.uploadedFiles.clear();
						NotificationStore.showSuccessMessage(
							Strings.general.saveSuccessMessage
						);
					} else {
						DialogStore.showError(
							Strings.general.errorDialogTitle,
							result.validationMessage
						);
					}
				})
				.catch((error) => {
					DialogStore.showError(
						Strings.general.errorDialogTitle,
						error
					);
				});
		}
	};

	@computed get areaAsideButtonTooltip() {
		return this.props.areaUiInfo.isCollapsed
			? Strings.documentManagement.collectionEdit
					.areaAsideButtonCollapsedTooltip
			: Strings.documentManagement.collectionEdit
					.areaAsideButtonExpandedTooltip;
	}

	getFilesForLanguage(lang: LanguageOption) {
		let arr = this.files.filter((file) =>
			file.language !== null
				? file.language === lang.value
				: lang.value === ""
		);
		let result: IObservableArray<DocumentManagement.FileDetails> =
			observable.array<DocumentManagement.FileDetails>();
		result.replace(arr);
		return result;
	}

	onFileUpload = (file: DocumentManagement.FileDetails) => {
		let uploadedFilesForContentGroup = this.uploadedFiles.filter(
			(f) => f.language == file.language
		);
		let uploadedFileForContentGroup =
			uploadedFilesForContentGroup.length > 0
				? uploadedFilesForContentGroup[0]
				: null;
		if (uploadedFileForContentGroup) {
			DialogStore.show(
				Strings.documentManagement.fileEdit.uploadSecondFileDialogTitle,
				Strings.documentManagement.fileEdit.uploadSecondFileDialogText
			).then((result) => {
				if (result === DialogResult.Yes) {
					this.files.remove(uploadedFileForContentGroup);
					this.uploadedFiles.remove(uploadedFileForContentGroup);
					this.files.unshift(file);
					this.uploadedFiles.push(file);
				}
			});
		} else {
			this.files.unshift(file);
			this.uploadedFiles.push(file);
		}
	};

	refs: {
		[key: string]: React.Component;
		fileUpload: FileUpload;
	};

	handleFileUploadClick = () => {
		this.refs.fileUpload.handleFileDeleted();
		this.refs.fileUpload.click();
	};

	handleFileUploadStart = () => {
		this.fileUploadInProgress = true;
	};

	handleFileUpload = (fileUploadInfo: FileUploadInfo) => {
		let name = fileUploadInfo.originalName as string;
		let nameParts = name.split(".");
		if (nameParts.length >= 2) {
			name = name.replace("." + nameParts[nameParts.length - 1], "");
		}
		let file = new DocumentManagement.FileDetails();
		file.guid = fileUploadInfo.guid;
		file.language = this.props.documentStore.selectedFileLanguage;
		file.mimeType = fileUploadInfo.mimeType;
		file.size = fileUploadInfo.size;
		file.name.value = name;
		file.name.isValid = true;
		file.originalName = fileUploadInfo.originalName;
		file.validFrom.isValid = true;
		file.validUntil.isValid = true;
		file.isValid = true;
		this.onFileUpload(file);
	};

	handleFileUploadEnd = () => {
		this.fileUploadInProgress = false;
	};

	render() {
		let tabIndex = 1;
		return (
			<AreaBody
				loading={AppStore.isBusyModelLoading}
				areaUiInfo={this.props.areaUiInfo}
			>
				<Toolbar>
					<ShowAreaAsideButton title={this.areaAsideButtonTooltip} />
					<SaveButton
						onClick={this.handleSaveClick}
						disabled={!this.canSave}
						testId="saveButton"
					/>
					<DeleteButton
						onClick={this.handleDelete}
						testId="deleteButton"
						disabled={
							!this.props.documentStore.selectedFiles.length ||
							this.props.documentStore.selectedFiles.length === 0
						}
						title={
							Strings.documentManagement.fileEdit
								.deleteButtonTooltip
						}
					/>
					<ChangeLanguageButton
						onClick={this.handleChangeLanguageClick}
						testId="changeLanguageButton"
						disabled={
							!this.props.documentStore.selectedFiles.length ||
							this.props.documentStore.selectedFiles.length === 0
						}
						title={
							Strings.documentManagement.fileEdit
								.changeLanguageButtonTooltip
						}
					/>
					<SelectionButton
						onClick={this.handleSelectClick}
						title={
							Strings.documentManagement.fileEdit
								.selectionButtonTooltip
						}
						pressed={this.props.documentStore.fileSelectionMode}
						testId="selectButton"
					/>
					<AddButton
						onClick={this.handleAdd}
						testId="addFileButton"
						title={
							Strings.documentManagement.fileEdit.addButtonTooltip
						}
					/>
					<span className="buttongroup is--buttongroup-right">
						<CloseButton
							onClick={this.handleCancel}
							testId="closeButton"
						/>
					</span>
				</Toolbar>
				<div className="area-grid__body-content">
					<div className="content-main">
						<h1>{Strings.documentManagement.fileEdit.title}</h1>
						<FileUpload
							ref="fileUpload"
							onloadstart={this.handleFileUploadStart}
							onfileupload={this.handleFileUpload}
							onloadend={this.handleFileUploadEnd}
							multiple={false}
							testId="fileUpload"
						/>
						{this.files.length > 0 ? (
							this.languageOptions.map((lang, index) => {
								let files = this.getFilesForLanguage(lang);
								let tabIndices = observable.array<number>();
								tabIndices.replace(
									files.map((file) => tabIndex++)
								);
								let uploadedFilesForContentGroup =
									this.uploadedFiles.filter(
										(f) => f.language === lang.value
									);
								let uploadedFileForContentGroup =
									uploadedFilesForContentGroup.length > 0
										? uploadedFilesForContentGroup[0]
										: null;
								return files.length > 0 ? (
									<LanguageGroup
										documentId={
											this.props.match.params.document
										}
										key={index}
										lang={lang}
										files={files}
										newFile={uploadedFileForContentGroup}
										tabIndices={tabIndices}
									/>
								) : null;
							})
						) : (
							<div
								className="inside__note is--clickable-note"
								onClick={this.handleAdd}
							>
								<span>
									{
										Strings.documentManagement.documentEdit
											.documentEmptyFilesResultText
									}
								</span>
							</div>
						)}
					</div>
				</div>
				{this.props.documentStore.fileLanguageWizardStore.isOpen ? (
					<WizardBox
						context={
							this.props.documentStore.fileLanguageWizardStore
						}
						type={this.wizardBoxType}
					>
						<FileLanguageSelection />
					</WizardBox>
				) : null}
			</AreaBody>
		);
	}
}

class LanguageOption {
	value: string;
	label: string;
	region: string;
}

interface LanguageGroupProps extends UiStore.IAreaUiInfo {
	documentId: string;
	files: IObservableArray<DocumentManagement.FileDetails>;
	tabIndices: IObservableArray<number>;
	lang: LanguageOption;
	documentStore?: DocumentStoreImpl;
	newFile: DocumentManagement.FileDetails;
}

@inject("documentStore")
@inject("areaUiInfo")
@observer
class LanguageGroup extends React.Component<LanguageGroupProps, {}> {
	get image(): JSX.Element {
		let img =
			this.props.lang.region == "001" ? (
				<img
					title={this.props.lang.label}
					src={
						"../../images/flags/" + this.props.lang.region + ".png"
					}
				/>
			) : (
				<div
					title={this.props.lang.label}
					className={
						"flag flag-" + this.props.lang.region.toLowerCase()
					}
				/>
			);
		return img;
	}

	@computed get fileListClass(): string {
		return this.props.documentStore.fileSelectionMode
			? "list-object fileEdit is--selection"
			: "list-object fileEdit";
	}

	render() {
		let format =
			Strings.language.name === "de-DE" || Strings.language.name === "de"
				? "DD.MM.YYYY"
				: "MM/DD/YYYY";
		return (
			<ContentGroup
				caption={this.props.lang.label}
				uiContext={this.props.areaUiInfo}
				image={this.image}
				testName="languageGroup"
				testId={this.props.lang.value}
			>
				<div className={this.fileListClass}>
					<ul>
						{this.props.files.map((file, index) => {
							return (
								<File
									documentId={this.props.documentId}
									key={file.guid}
									file={file}
									tabIndex={this.props.tabIndices[index]}
								/>
							);
						})}
					</ul>
				</div>
			</ContentGroup>
		);
	}
}

interface FileProps {
	documentId: string;
	file: DocumentManagement.FileDetails;
	tabIndex: number;
	documentStore?: DocumentStoreImpl;
}

@inject("documentStore")
@observer
class File extends React.Component<FileProps, {}> {
	@computed get isSelected(): boolean {
		return this.props.documentStore.selectedFiles.some(
			(f) => f.guid === this.props.file.guid
		);
	}

	@action handleSelect = (e: React.MouseEvent<any>) => {
		if (this.props.documentStore.fileSelectionMode) {
			if (
				!this.props.documentStore.selectedFiles.remove(this.props.file)
			) {
				this.props.documentStore.selectedFiles.push(this.props.file);
			}
			e.preventDefault();
		}
	};

	onNameChange = () => {
		let limit = 260;
		if (this.props.file.name.value.length > limit) {
			this.props.file.name.isValid = false;
			this.props.file.name.validationMessage = Utilities.format(
				Strings.documentManagement.fileEdit.characterLimitErrorMessage,
				limit
			);
		} else if (!this.props.file.name.value) {
			this.props.file.name.isValid = false;
			this.props.file.name.validationMessage = null;
		} else {
			this.props.file.name.isValid = true;
			this.props.file.name.validationMessage = null;
		}
	};

	render() {
		return (
			<li
				className={this.props.file.isActive ? "active" : ""}
				data-test-name="documentFile"
				data-test-id={this.props.file.name.value}
				key={this.props.file.guid}
				title={Utilities.format(
					Strings.documentManagement.fileEdit.fileTooltip,
					`${Math.ceil(this.props.file.size / 1024)} kB`,
					this.props.file.created ? this.props.file.created : "-"
				)}
			>
				<div>
					<div className="list-object__tools">
						<div className="tinytool">
							<InputDateComponent
								model={this.props.file.validFrom}
								locale={Strings.language.name}
								clearable={true}
								placeHolder={Strings.reader.validFromHeader}
							/>
							<InputDateComponent
								model={this.props.file.validUntil}
								locale={Strings.language.name}
								clearable={true}
								placeHolder={Strings.reader.validUntilHeader}
							/>
							<FileLinkButton
								onClick={() =>
									AppStore.keepOnPage(
										Utilities.getDeepLinkReaderUrl(
											this.props.documentId,
											this.props.file.language,
											this.props.file.name.value,
											this.props.file.mimeType
										),
										true
									)
								}
								title={
									Strings.documentManagement.fileEdit
										.readerButtonTooltip
								}
								disabled={!this.props.file.created}
							/>
						</div>
					</div>
					<SelectCheckbox
						onClick={this.handleSelect}
						checked={this.isSelected}
						disabled={false}
					/>
					<div className="list-object__name">
						<MimeTypeIcon
							mimeType={Utilities.getDisplayMimetype(
								this.props.file.mimeType
							)}
							iconType={IconType.Png16}
						/>
						<InputTextComponent
							model={this.props.file.name}
							testName="fileName"
							tabIndex={this.props.tabIndex}
							focus={this.props.tabIndex === 0}
							onChange={this.onNameChange}
						/>
					</div>
				</div>
			</li>
		);
	}
}
